/**
 * List tabs — the client twin of the engine's `list_tabs.list_tabs_of`
 * (Python) and `ListTabs.of` (Java). Validates `list_view.tabs` and expands
 * the one-field shortcut into tabs, with the SAME rules, so the screen knows
 * which tabs to draw and which one to ask for before the first response
 * arrives. The server stays the authority: it reads the chosen tab's
 * conditions from the saved model and answers an unknown id with
 * TAB_NOT_FOUND. Held to the engine by `test-script/list_tabs_fixtures/`
 * (`test_list_tabs_ui.mjs`).
 */
import type { ColumnDef, SqlModel } from '../types'
import { ALL_TAB_ID, ALL_TAB_LABEL, TAB_LIMITS, TAB_OPS } from './listScreenConfig'

export interface TabCondition {
  column: string
  op: string
  value?: string | string[]
  from?: string
  to?: string
}

export interface NormalizedTab {
  id: string
  label: string
  color?: string
  when: TabCondition[]
}

export interface NormalizedTabs {
  show_all: boolean
  counts: boolean
  default: string
  items: NormalizedTab[]
}

/** One tab as the row draws it (the server's `list_tabs` echo, or the model's
 *  tabs before the first response). */
export interface TabEcho { id: string; label: string; color?: string; count?: number }

const VALUELESS = new Set(['is_empty', 'is_not_empty'])

const s = (v: unknown): string => (v == null ? '' : String(v).trim())

/** A column's database name, as the engine resolves it (`column_db_name`). */
export function columnDbName(col: Partial<ColumnDef> | null | undefined): string {
  const raw = s((col as { db_name?: string } | null)?.db_name || col?.name)
  return raw.replace(/\s+/g, '_').toLowerCase()
}

function columnsOf(sm: SqlModel): ColumnDef[] {
  const raw = (sm as unknown as { columns?: unknown[] }).columns || []
  const out: ColumnDef[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const col = ((item as { column?: unknown }).column ?? item) as ColumnDef
    if (col && typeof col === 'object') out.push(col)
  }
  return out
}

/** The form's main-table columns (a tab is a WHERE on the main table). */
export function mainTableColumns(sm: SqlModel): ColumnDef[] {
  const smr = sm as unknown as { main_table?: string; table_name?: string }
  const main = s(smr.main_table || smr.table_name).replace(/\s+/g, '_').toLowerCase()
  return columnsOf(sm).filter((c) => {
    const cr = c as unknown as { sql_expression?: string; uda?: unknown; table_name?: string }
    if (s(cr.sql_expression) || cr.uda) return false
    const src = s(cr.table_name).toLowerCase()
    return !src || src === main
  })
}

/** The column's fixed choices (`edit_mask` ddlb / radio values). */
export function picklistOf(col: ColumnDef | null | undefined): Array<{ data: string; display: string }> {
  const em = (col as unknown as { edit_mask?: { mask?: string; values?: unknown[] } } | null)?.edit_mask
  if (!em || typeof em !== 'object') return []
  if (!['ddlb', 'radio'].includes(s(em.mask).toLowerCase())) return []
  const out: Array<{ data: string; display: string }> = []
  const seen = new Set<string>()
  for (const v of em.values || []) {
    if (!v || typeof v !== 'object') continue
    const d = (v as { data?: unknown }).data
    if (d == null) continue
    const data = String(d)
    if (seen.has(data)) continue
    seen.add(data)
    out.push({ data, display: s((v as { display?: unknown }).display) || data })
  }
  return out
}

function displayColor(col: ColumnDef, value: string): string | undefined {
  const disp = (col as unknown as { display?: { type?: string; map?: unknown[]; defaultColor?: string } }).display
  if (!disp || !['status_tag', 'status_dot'].includes(s(disp.type))) return undefined
  const want = value.trim().toLowerCase()
  for (const e of disp.map || []) {
    if (e && typeof e === 'object' && s((e as { when?: unknown }).when).toLowerCase() === want) {
      const c = s((e as { color?: unknown }).color)
      if (c) return c
    }
  }
  return s(disp.defaultColor) || undefined
}

function normCondition(raw: unknown, cols: Map<string, ColumnDef>): TabCondition | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const column = s(r.column).toLowerCase()
  if (!column || !cols.has(column)) return null
  const op = s(r.op || 'eq').toLowerCase()
  if (!TAB_OPS.has(op)) return null
  const out: TabCondition = { column, op }
  if (VALUELESS.has(op)) return out
  if (op === 'between') {
    const lo = 'from' in r ? r.from : r.default_from
    const hi = 'to' in r ? r.to : r.default_to
    if (s(lo) === '' || s(hi) === '') return null
    out.from = s(lo)
    out.to = s(hi)
    return out
  }
  const val = 'value' in r ? r.value : r.default_value
  if (Array.isArray(val)) {
    const vals = val.map(s).filter((v) => v !== '')
    if (!vals.length) return null
    out.value = op === 'in' ? vals : vals[0]
    return out
  }
  if (s(val) === '') return null
  if (op === 'in') {
    const vals = String(val).split(',').map((p) => p.trim()).filter(Boolean)
    if (!vals.length) return null
    out.value = vals
  } else {
    out.value = s(val)
  }
  return out
}

/** The usable tabs of a MAIN form, or null (no tab row). Never throws. */
export function listTabsOf(sm: SqlModel | null | undefined): NormalizedTabs | null {
  if (!sm) return null
  const lv = (sm as unknown as { list_view?: { tabs?: unknown } }).list_view
  const tb = (lv && typeof lv === 'object') ? lv.tabs : undefined
  if (!tb || typeof tb !== 'object' || Array.isArray(tb)) return null
  const t = tb as Record<string, unknown>
  if (t.enabled === false) return null
  const cols = new Map<string, ColumnDef>()
  for (const c of mainTableColumns(sm)) {
    const k = columnDbName(c)
    if (k) cols.set(k, c)
  }
  const items: NormalizedTab[] = []
  const seen = new Set<string>()
  const add = (id: string, label: string, color: string | undefined, when: TabCondition[]) => {
    if (items.length >= TAB_LIMITS.maxTabs) return
    if (!id || id.length > TAB_LIMITS.maxIdLength || id === ALL_TAB_ID || seen.has(id)) return
    if (!when.length || when.length > TAB_LIMITS.maxConditionsPerTab) return
    seen.add(id)
    const entry: NormalizedTab = { id, label: (label || id).slice(0, TAB_LIMITS.maxLabelLength), when }
    if (color) entry.color = color
    items.push(entry)
  }
  const rawItems = t.items
  if (Array.isArray(rawItems) && rawItems.length) {
    for (const it of rawItems) {
      if (!it || typeof it !== 'object') continue
      const ir = it as Record<string, unknown>
      if (!Array.isArray(ir.when) || !ir.when.length) continue
      const when = ir.when.map((c) => normCondition(c, cols))
      if (when.some((c) => c === null)) continue
      add(s(ir.id), s(ir.label), s(ir.color) || undefined, when as TabCondition[])
    }
  } else {
    const column = s(t.column).toLowerCase()
    const col = cols.get(column)
    const choices = picklistOf(col)
    if (col && choices.length) {
      const wanted = t.values
      let ordered = choices
      if (Array.isArray(wanted) && wanted.length) {
        const byData = new Map(choices.map((c) => [c.data, c]))
        ordered = wanted.filter((v) => v != null && byData.has(String(v)))
          .map((v) => byData.get(String(v))!)
      }
      for (const ch of ordered) {
        add(ch.data, ch.display, displayColor(col, ch.data),
          [{ column, op: 'eq', value: ch.data }])
      }
    }
  }
  if (!items.length) return null
  const showAll = t.show_all !== false
  let def = s(t.default) || ALL_TAB_ID
  if (def !== ALL_TAB_ID && !seen.has(def)) def = ALL_TAB_ID
  if (def === ALL_TAB_ID && !showAll) def = items[0].id
  return { show_all: showAll, counts: t.counts !== false, default: def, items }
}

/** The tab row before the server has answered: All (when shown) + every tab. */
export function tabsEcho(tabs: NormalizedTabs): TabEcho[] {
  const out: TabEcho[] = []
  if (tabs.show_all) out.push({ id: ALL_TAB_ID, label: ALL_TAB_LABEL })
  for (const t of tabs.items) out.push(t.color ? { id: t.id, label: t.label, color: t.color } : { id: t.id, label: t.label })
  return out
}

/** The tab to open: the remembered one when it still exists, else the
 *  model's default. */
export function initialTab(tabs: NormalizedTabs | null, remembered?: string | null): string | null {
  if (!tabs) return null
  const r = s(remembered)
  if (r && ((r === ALL_TAB_ID && tabs.show_all) || tabs.items.some((t) => t.id === r))) return r
  return tabs.default
}
